// Olympe
import { registerBrick } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';

// React
import React, {useCallback, useRef, useState} from 'react';
import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';

// Internal
import { initData } from '../common/internal/data';
import { initWrappers } from '../common/internal/wrappers';
import { initFilteredColumns, useColumns, handleOnColumnResized, handleOnColumnsReordered } from '../common/internal/columns';
import { initRows, useRows } from '../common/internal/rows';
import { initBox, useBox } from '../common/internal/box';
import { handleOnCellsChanged } from '../common/internal/changes';
import { initTriggers, useRedraw } from '../common/internal/triggers';
import { initScroll, useRedrawAfterScroll } from '../common/internal/scroll';
import { initCells } from '../common/internal/cells';

export default class RGReactGrid extends ReactBrick {

    /**
     * @override
     */
    init($) {
        // Data/Config to Rows/Columns flow
        initCells($);
        const _reload = initTriggers($);
        const _data = initData($, _reload);
        const _wrappers = initWrappers($, _data);
        const _filteredColumns = initFilteredColumns($, _wrappers);
        initRows($, _wrappers, _filteredColumns);

        // Bounding box flow
        initBox($);

        // Scroll 
        initScroll($);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return () => {
            // Display
            const box = useBox($);
            const hidden = useProperty($, 'Hidden');
            const allowContentOverflow = useProperty($, 'Allow Content Overflow');
            const fontSize = useProperty($, 'Font Size');

            // Data
            const rows = useRows($);
            const columns = useColumns($);

            // Behaviors
            const rangeSelection = useProperty($, 'Enable Range Selection');
            const rowSelection = useProperty($, 'Enable Row Selection');
            const colSelection = useProperty($, 'Enable Column Selection');
            const fillHandle = useProperty($, 'Enable Fill Handle');

            // Sticky
            const stickTop = useProperty($, 'Sticky Top Rows');
            const stickBottom = useProperty($, 'Sticky Bottom Rows');
            const stickLeft = useProperty($, 'Sticky Left Columns');
            const stickRight = useProperty($, 'Sticky Right Columns');

            // Redraw hook
            const containerRef = useRef();
            useRedrawAfterScroll($, containerRef);
            useRedraw($);

            // Compute the global position of the grid to set the editor cell in the right place
            const [gridPosition, setGridPosition] = useState({ x: 0, y: 0 });
            const updateBoxPosition = useCallback(() => {
                const rect = containerRef.current?.getBoundingClientRect();
                setGridPosition({ x: rect.left, y: rect.top });
            }, []);

            // Render the grid
            return (
                <div ref={containerRef}>
                    {/* ReactGrid component */}
                    <ReactGrid
                        // Data
                        rows={rows}
                        columns={columns}
                        onCellsChanged={handleOnCellsChanged.bind(undefined, $)}
                        enableFillHandle={fillHandle}
                        onFocusLocationChanged={updateBoxPosition} // Compute the global position of the grid to set the editor cell in the right place

                        // Sticky
                        stickyLeftColumns={stickLeft}
                        stickyRightColumns={stickRight}
                        stickyTopRows={stickTop}
                        stickyBottomRows={stickBottom}

                        // Selection
                        enableRangeSelection={rangeSelection}
                        enableColumnSelection={colSelection}
                        enableRowSelection={rowSelection}

                        // Resizing
                        onColumnResized={handleOnColumnResized.bind(undefined, $)}

                        // Reordering
                        onColumnsReordered={handleOnColumnsReordered.bind(undefined, $)}
                    />

                    {/* Styling */}
                    <style jsx>{`
                        // Wrapper styling
                        div {
                            display: ${hidden ? 'none' : 'inline-block'};
                            width: ${box.width}px;
                            height: ${box.height}px;
                            overflow: ${allowContentOverflow ? 'visible' : 'scroll'};
                            font-family: 'Roboto', sans-serif;
                        }

                        // Hack: put the editor cell in the right place, based on the computed global position of the grid
                        div :global(.rg-celleditor) {
                            font-size: ${fontSize}px !important;
                            transform: translate(${-gridPosition.x}px, ${-gridPosition.y}px);
                        }

                        // Pane styling
                        div :global(.rg-pane) {
                            box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
                            background-color: #fff; 
                        }

                        // General cell styling
                        div :global(.rg-cell) {
                            padding: 10px;
                            font-size: ${fontSize}px !important;
                            border-bottom: 1px solid #e0e0e0;
                            color: rgba(0, 0, 0, 0.87);
                            transition: background-color 0.2s ease-in-out;
                        }

                        // Header cell styling to match MUI Table header
                        div :global(.rg-header-cell) {
                            background-color: #f5f5f5;
                            font-weight: 500;
                            color: rgba(0, 0, 0, 0.87);
                            font-size: 14px;
                            border-bottom: 1px solid #e0e0e0;
                            padding: 12px;
                        }

                        // Number cell styling
                        div :global(.rg-number-cell) {
                            text-align: right;
                            padding-right: 14px;
                            color: rgba(0, 0, 0, 0.87);
                            padding-left: 12px;
                        }

                        // Text cell styling
                        div :global(.rg-text-cell) {
                            text-align: left;
                            color: rgba(0, 0, 0, 0.87);
                            padding-left: 12px;
                        }

                        // Hover effect for cells to look like MUI Table rows
                        div :global(.rg-cell):hover {
                            background-color: #f5f5f5;
                        }

                        // Scrollbar styling to match MUI's scrollbars
                        div::-webkit-scrollbar {
                            display: block;
                            width: 8px;
                            height: 8px;
                        }
                        div::-webkit-scrollbar-thumb {
                            background-color: rgba(0, 0, 0, 0.2);
                            border-radius: 4px;
                        }
                        div::-webkit-scrollbar-track {
                            background-color: #f0f0f0;
                        }
                    `}</style>
                </div>
            );
        };
    }
}

registerBrick('019154c4600798f105aa', RGReactGrid);
